import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Droplet,
  Flame,
  Mountain,
  TrendingUp,
  ShieldCheck,
  Database,
  Globe2,
  LineChart,
  ArrowRight,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const SectionEyebrow = ({ children }) => (
  <div className="text-xs font-mono uppercase tracking-widest text-[#C85A32] mb-4">
    {children}
  </div>
);

const NewsletterForm = ({ variant = "hero" }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = t("newsletter.errors.name_required");
    if (!values.email.trim()) errs.email = t("newsletter.errors.email_required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      errs.email = t("newsletter.errors.email_invalid");
    if (!values.consent) errs.consent = t("newsletter.errors.consent_required");
    return errs;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    // No backend call — lead is captured in-page; redirect to thank you page.
    setTimeout(() => {
      navigate("/thank_you");
    }, 150);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-[#CBD5E1] p-6 sm:p-8"
      data-testid={variant === "hero" ? "hero-newsletter-form" : "newsletter-form"}
      noValidate
    >
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label
            htmlFor={`${variant}-name`}
            className="block text-xs font-mono uppercase tracking-widest text-[#475569] mb-2"
          >
            {t("newsletter.form.name_label")}
          </label>
          <input
            id={`${variant}-name`}
            type="text"
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            placeholder={t("newsletter.form.name_placeholder")}
            className="w-full h-11 px-3 border border-[#CBD5E1] focus:outline-none focus:border-[#C85A32] bg-white text-[#0F172A]"
            data-testid={`${variant}-input-name`}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-[#C85A32]" data-testid={`${variant}-error-name`}>
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor={`${variant}-email`}
            className="block text-xs font-mono uppercase tracking-widest text-[#475569] mb-2"
          >
            {t("newsletter.form.email_label")}
          </label>
          <input
            id={`${variant}-email`}
            type="email"
            value={values.email}
            onChange={(e) => setValues({ ...values, email: e.target.value })}
            placeholder={t("newsletter.form.email_placeholder")}
            className="w-full h-11 px-3 border border-[#CBD5E1] focus:outline-none focus:border-[#C85A32] bg-white text-[#0F172A]"
            data-testid={`${variant}-input-email`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-[#C85A32]" data-testid={`${variant}-error-email`}>
              {errors.email}
            </p>
          )}
        </div>
        <label className="flex items-start gap-2 text-xs text-[#475569] leading-relaxed">
          <input
            type="checkbox"
            checked={values.consent}
            onChange={(e) => setValues({ ...values, consent: e.target.checked })}
            className="mt-0.5 h-4 w-4 accent-[#C85A32]"
            data-testid={`${variant}-input-consent`}
          />
          <span>{t("newsletter.form.consent")}</span>
        </label>
        {errors.consent && (
          <p className="text-xs text-[#C85A32]" data-testid={`${variant}-error-consent`}>
            {errors.consent}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center justify-center gap-2 h-11 px-5 bg-[#0B192C] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#1E293B] disabled:opacity-70"
          data-testid={`${variant}-submit-button`}
        >
          {submitting ? t("newsletter.form.loading") : t("newsletter.form.submit")}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
};

const Home = () => {
  const { t, dict } = useI18n();

  const indicators = ["vaca_muerta", "lithium", "rigi", "gas"];
  const whyItems = dict.why.items;
  const steps = dict.methodology.steps;
  const pillars = dict.transparency.pillars;

  const sectors = [
    {
      id: "lithium",
      Icon: Droplet,
      key: "sectors.lithium",
      img: "/images/lithium-salt-flats.jpg",
    },
    {
      id: "vaca_muerta",
      Icon: Flame,
      key: "sectors.vaca_muerta",
      img: "/images/vaca-muerta.jpg",
    },
    {
      id: "mining",
      Icon: Mountain,
      key: "sectors.mining",
      img: "/images/mining.jpg",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-[#0B192C] text-white border-b border-[#1E293B]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <SectionEyebrow>
                <span className="text-[#C85A32]">{t("hero.eyebrow")}</span>
              </SectionEyebrow>
              <h1 className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white">
                {t("hero.title")}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#CBD5E1] leading-relaxed">
                {t("hero.subtitle")}
              </p>
              <p className="mt-4 text-sm sm:text-base text-[#94A3B8] leading-relaxed">
                {t("hero.body_1")}
              </p>
              <p className="mt-3 text-sm sm:text-base text-[#94A3B8] leading-relaxed">
                {t("hero.body_2")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#newsletter"
                  className="inline-flex items-center gap-2 h-11 px-5 bg-[#C85A32] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#B24E2A]"
                  data-testid="hero-cta-primary"
                >
                  {t("hero.cta_primary")}
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#sectors"
                  className="inline-flex items-center gap-2 h-11 px-5 border border-[#CBD5E1] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#1E293B]"
                  data-testid="hero-cta-secondary"
                >
                  {t("hero.cta_secondary")}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[#1E293B]">
                <img
                  src="/images/lithium-salt-flats.jpg"
                  alt={t("hero.image_alt")}
                  className="w-full h-56 sm:h-72 lg:h-80 object-cover"
                  data-testid="hero-image"
                />
                <div className="p-4 bg-[#1E293B] border-t border-[#334155]">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#C85A32]">
                    Salar de Olaroz — Jujuy, Argentina
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDICATORS */}
      <section className="bg-white border-b border-[#CBD5E1]" data-testid="indicators-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <SectionEyebrow>{t("indicators.eyebrow")}</SectionEyebrow>
          <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-[#0B192C] max-w-3xl">
            {t("indicators.title")}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-3xl">
            {t("indicators.subtitle")}
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-[#CBD5E1]">
            {indicators.map((k, i) => {
              const item = dict.indicators.items[k];
              return (
                <div
                  key={k}
                  className={
                    "p-6 " +
                    (i < indicators.length - 1
                      ? "border-b sm:border-b lg:border-b-0 lg:border-r border-[#CBD5E1] "
                      : "") +
                    (i === 1 ? "sm:border-r-0 lg:border-r " : "")
                  }
                  data-testid={`indicator-${k}`}
                >
                  <div className="text-xs font-mono uppercase tracking-widest text-[#475569]">
                    {item.label}
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-serif font-bold text-3xl text-[#0B192C]">
                      {item.value}
                    </span>
                    <span className="text-xs font-mono text-[#475569]">{item.unit}</span>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-mono text-[#0284C7]">
                    <TrendingUp className="h-3 w-3" /> {item.delta}
                  </div>
                  <div className="mt-4 text-xs text-[#475569] leading-relaxed">{item.note}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" className="bg-[#F8FAFC] border-b border-[#CBD5E1]" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <SectionEyebrow>{t("sectors.eyebrow")}</SectionEyebrow>
          <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-[#0B192C] max-w-3xl">
            {t("sectors.title")}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-3xl">
            {t("sectors.subtitle")}
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {sectors.map((s) => {
              const Icon = s.Icon;
              return (
                <article
                  key={s.id}
                  className="bg-white border border-[#CBD5E1] flex flex-col"
                  data-testid={`sector-card-${s.id}`}
                >
                  <div className="relative h-48 bg-[#0B192C] overflow-hidden">
                    <img
                      src={s.img}
                      alt={t(`${s.key}.image_alt`)}
                      className="w-full h-full object-cover opacity-90"
                    />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#0B192C] text-white px-2 py-1 text-xs font-mono uppercase tracking-widest">
                      <Icon className="h-3 w-3 text-[#C85A32]" />
                      {t(`${s.key}.region`)}
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-serif font-bold text-xl text-[#0B192C]">
                      {t(`${s.key}.name`)}
                    </h3>
                    <p className="mt-3 text-sm text-[#475569] leading-relaxed flex-1">
                      {t(`${s.key}.description`)}
                    </p>
                    <div className="mt-5 pt-4 border-t border-[#CBD5E1] text-xs font-mono uppercase tracking-widest text-[#C85A32]">
                      {t(`${s.key}.highlight`)}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-white border-b border-[#CBD5E1]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <SectionEyebrow>{t("why.eyebrow")}</SectionEyebrow>
          <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-[#0B192C] max-w-3xl">
            {t("why.title")}
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyItems.map((item, idx) => {
              const icons = [Database, Globe2, LineChart, ShieldCheck];
              const Icon = icons[idx % icons.length];
              return (
                <div
                  key={idx}
                  className="border border-[#CBD5E1] p-6 flex gap-4"
                  data-testid={`why-item-${idx}`}
                >
                  <div className="shrink-0 h-10 w-10 border border-[#C85A32] inline-flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#C85A32]" />
                  </div>
                  <div>
                    <h3 className="font-serif font-bold text-lg text-[#0B192C]">{item.title}</h3>
                    <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section id="methodology" className="bg-[#0B192C] text-white border-b border-[#1E293B]" data-testid="methodology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionEyebrow>{t("methodology.eyebrow")}</SectionEyebrow>
              <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-white">
                {t("methodology.title")}
              </h2>
              <p className="mt-4 text-base text-[#CBD5E1] leading-relaxed">
                {t("methodology.subtitle")}
              </p>
              <div className="mt-8 border border-[#1E293B]">
                <img
                  src="/images/financial-chart.jpg"
                  alt="Voryzix analytical dashboard"
                  className="w-full h-56 object-cover opacity-90"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <ol className="space-y-4">
                {steps.map((s) => (
                  <li
                    key={s.number}
                    className="border border-[#1E293B] p-5 flex gap-5"
                    data-testid={`methodology-step-${s.number}`}
                  >
                    <div className="shrink-0 font-serif font-bold text-3xl text-[#C85A32] leading-none w-14">
                      {s.number}
                    </div>
                    <div>
                      <h3 className="font-serif font-bold text-lg text-white">{s.title}</h3>
                      <p className="mt-2 text-sm text-[#CBD5E1] leading-relaxed">
                        {s.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="bg-white border-b border-[#CBD5E1]" data-testid="transparency-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <SectionEyebrow>{t("transparency.eyebrow")}</SectionEyebrow>
              <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-[#0B192C] max-w-3xl">
                {t("transparency.title")}
              </h2>
              <p className="mt-4 text-base text-[#1E293B] leading-relaxed">
                {t("transparency.body_1")}
              </p>
              <p className="mt-3 text-base text-[#1E293B] leading-relaxed">
                {t("transparency.body_2")}
              </p>
              <div className="mt-8 space-y-3">
                {pillars.map((p, i) => (
                  <div
                    key={i}
                    className="border-l-2 border-[#C85A32] pl-4 py-2"
                    data-testid={`transparency-pillar-${i}`}
                  >
                    <h4 className="font-serif font-bold text-base text-[#0B192C]">{p.title}</h4>
                    <p className="mt-1 text-sm text-[#475569] leading-relaxed">{p.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[#CBD5E1]">
                <img
                  src="/images/gas-pipeline.jpg"
                  alt={t("transparency.image_alt")}
                  className="w-full h-64 lg:h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[#F8FAFC]" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <SectionEyebrow>{t("newsletter.eyebrow")}</SectionEyebrow>
              <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl text-[#0B192C]">
                {t("newsletter.title")}
              </h2>
              <p className="mt-4 text-base text-[#475569] leading-relaxed">
                {t("newsletter.subtitle")}
              </p>
              <div className="mt-8 border-l-2 border-[#C85A32] pl-4">
                <div className="text-xs font-mono uppercase tracking-widest text-[#C85A32]">
                  {t("indicators.items.rigi.label")}
                </div>
                <div className="mt-1 font-serif font-bold text-xl text-[#0B192C]">
                  {t("indicators.items.rigi.value")}{" "}
                  <span className="text-xs font-mono text-[#475569]">
                    {t("indicators.items.rigi.unit")}
                  </span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6">
              <NewsletterForm variant="hero" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
